import { apiCacheKeys } from '@doltech/core/lib/api/api.urls';
import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { FooterBanner } from '@doltech/ui/lib/banner/component/HeaderBanner';
import { Avatar } from '@doltech/ui/lib/figma/Avatar';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { EditorDailyNewsUserViewStyles } from '@doltech/ui/lib/figma/BlogEditor/BlogEditorStyle.presets';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { CardNews } from '@doltech/ui/lib/figma/DailyLearning/UserView/CardNews';
import { PostListing } from '@doltech/ui/lib/figma/DailyLearning/UserView/PostListing';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { InteractionBottomNavigation } from '@doltech/ui/lib/figma/Layout/Landing/BottomNavigation/InteractionBottomNavigation';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { LandingLayoutDailyLearning } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/LandingLayoutDailyLearning';
import { BlogSocialButton } from '@doltech/ui/lib/figma/Layout/Landing/SocialButton/BlogSocialButton';
import { Link } from '@doltech/ui/lib/figma/Link';
import { ReferralShareBanner } from '@doltech/ui/lib/figma/ReferralShare/ReferralShareBanner';
import { Skeleton } from '@doltech/ui/lib/figma/Skeleton/Skeleton';
import { withWrapper } from '@doltech/ui/lib/hocs/withWrapper';
import { responsiveBreakPoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';

import { PreviewMain } from '../../../../ielts-sample/sample.utils';
import { withSamplePreviewStep } from '../../../../ielts-sample/Writing/Steps/SamplePreview';
import { getRecentNewsList } from '../../../apis/daily-learning-news.api';
import { DailyNewsHeader } from '../../../components/DailyNewsHeader';
import { TitleH2DailyLearning } from '../../../marketer/Preview/components/TitleH2DailyLearning';
import { gridSpacing } from '../../../marketer/Preview/LandingDailyLearningPreview';

import dynamic from 'next/dynamic';
import { DailyNewsBlogEditorReadOnly } from '../../../components/DailyNewsBlogEditorReadOnly';
import { ContactDol } from '@doltech/ui/lib/figma/ContactDol/ContactDol';

const DailyLearningLeftSideBar = dynamic<any>(
  () => import('../Preview/DailyLearningLeftSideBar').then((mod) => mod.DailyLearningLeftSideBar),
  {
    ssr: false,
  }
);

const SideBarPageList = dynamic<any>(
  () => import('../../../components/SideBarPageList').then((mod) => mod.SideBarPageList),
  {
    ssr: false,
  }
);

const StyledDailyNewsBlogEditorReadOnly = styled(DailyNewsBlogEditorReadOnly)`
  border-radius: 0 0 8px 8px;
  margin: 0 auto;
  width: 100%;
  padding: 0 24px;
  background-color: transparent;

  ${fromScreen(541)} {
    width: 576px;
  }
`;

const Main = styled(PreviewMain)`
  background-color: transparent;
  ${gridSpacing};
  ${EditorDailyNewsUserViewStyles}
  .limit-content {
    .blog-editor-body {
      height: 600px;
      overflow: hidden;
    }
  }
  .toc-layout {
    color: ${colorsV2.white100};
    .content-item {
      color: ${colorsV2.white80};
      &.active {
        color: ${colorsV2.primary100};
      }
    }
  }

  .news-main-content {
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(0, 1fr);
  }

  .sidebar-holder {
    width: 576px;
    margin: 0 auto;
  }

  .header-with-back-btn {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, max-content);
    align-items: center;
    color: ${colorsV2.white100};
    gap: 12px;
    margin-bottom: 20px;
    cursor: pointer;
    .icon-wrapper {
      font-size: 24px;
      line-height: 0;
    }
  }

  .single-card-swiper {
    ${fromScreen(528)} {
      width: 528px;
    }
  }

  .header-info {
    display: grid;
    gap: 8px;
  }

  .header-image {
    width: 100%;
    position: relative;
  }

  .right-sidebar-preview {
    display: none;
    ${fromScreen(1280)} {
      display: block;
      position: fixed;
    }
  }

  .popular-news-post-listing,
  .recent-news-post-listing {
    display: grid;
    gap: 16px;
    .card-news {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 80px;
      gap: 24px;
      .card-news-cover {
        order: 2;
        height: 80px;
      }
      .card-news-content {
        order: 1;
      }

      @media ${responsiveBreakPoints['above-528']} {
        grid-template-columns: minmax(0, 1fr) 160px;
        .card-news-cover {
          height: 106px;
        }
      }
    }
  }

  .vocab-idea-editor-container {
    background-color: transparent;
    .dol-editor {
      color: ${colorsV2.white100};
    }
  }

  .extra-sections {
    background-color: ${colorsV2.gray10};
    padding: 64px 0;
    div.level {
      border-color: ${colorsV2.gray20};
    }
  }

  .section-container {
    padding: 0 16px;
    ${gridSpacing};
    ${fromScreen(528)} {
      margin: 0 auto;
      padding: 0;
      width: 528px;
    }
  }
  .section-block {
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
  }

  .section-author-actions {
    color: ${colorsV2.white100};
  }

  #footer-author {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) max-content;
    ${fromScreen(528)} {
      grid-template-columns: 44px minmax(0, 1fr) max-content;
    }
    align-items: center;
    gap: 12px;
    .ant-avatar {
      width: 40px !important;
      height: 40px !important;
      @media ${responsiveBreakPoints['above-528']} {
        width: 44px !important;
        height: 44px !important;
      }
    }
    .author-info {
      .author-info-title {
        font-size: 12px !important;
        line-height: 20px !important;
      }
      .author-info-link {
        &:hover {
          text-decoration: underline;
        }
      }
      .author-info-name {
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 20px !important;
        color: ${colorsV2.primary100};
      }

      ${fromScreen(528)} {
        .author-info-title {
          font-size: 14px !important;
          line-height: 20px !important;
        }
        .author-info-name {
          font-size: 16px !important;
          line-height: 24px !important;
        }
      }
    }
  }
  .sticky-on {
    position: relative;
  }
  .desktop-sidebar {
    display: none;
    ${fromScreen(1280)} {
      display: grid;
    }
  }
`;

export const DailyNewsTocId = {
  TIN_TUC: 'tin-tuc',
  TONG_HOP_VOCAB: 'tong-hop-vocab',
  QUIZZ: 'quizz',
  EXTRA_LEARNING_POINT: 'extra-learning-point',
} as const;

const DailyNewsPreviewBase = (props: any) => {
  const {
    internalPageLink,
    seoData,
    popularNews,
    recentNews,
    excludedPopularNewsIds = [],
    isPreview,
  } = props;
  const { appScrollRef } = useAppScrollRef();

  return (
    <Main>
      <ContactDol />
      <div id="blog-content-container" className={cl('news-main-content', 'blog-editor-container')}>
        <DailyNewsHeader {...props} />
        <div className="sidebar-holder left-sidebar-stick right-sidebar-stick" />
        <StyledDailyNewsBlogEditorReadOnly {...props} />
      </div>
      {!isPreview && (
        <div id={DailyNewsTocId.TIN_TUC} className="section-container">
          <div>
            <Separator mb={24} variant="horizontal" backgroundColor="gray40" />
            <Grid columns={1} gap="32px">
              <div id="footer-author" className="section-block">
                <Avatar
                  user={{
                    avatar: {
                      src: dolDinhLucAuthor.avatarUrl,
                      alt: dolDinhLucAuthor.name,
                    },
                  }}
                />
                <div className="author-info">
                  <Typography.Paragraph className="author-info-title" color="gray100">
                    Tác giả
                  </Typography.Paragraph>
                  <Link href={dolDinhLucAuthor.authorAboutURL} className="author-info-link">
                    <Typography.Paragraph className="author-info-name">
                      {dolDinhLucAuthor.name}
                    </Typography.Paragraph>
                  </Link>
                </div>
                <div className={cl('social-buttons-wrapper')}>
                  {seoData && (
                    <BlogSocialButton
                      align="horizontal"
                      actions={['copy-link', 'facebook', 'clap']}
                      showText={false}
                      seoData={
                        seoData || {
                          id: 'preview',
                          url: '/',
                        }
                      }
                      pageData={null}
                    />
                  )}
                </div>
              </div>
              <FooterBanner isDalyNew />
            </Grid>
          </div>
        </div>
      )}

      <div id="extra-sections" className="extra-sections">
        <div className="section-container">
          {popularNews?.content?.length > 0 && (
            <>
              <div id="pho-bien" className="section-block">
                <TitleH2DailyLearning className="font-inter">Phổ biến</TitleH2DailyLearning>
                <PostListing
                  className="popular-news-post-listing"
                  apiKey={[apiCacheKeys.publicService.GET_POPULAR_NEWS_LIST, seoData.id]}
                  apiFn={async () => {
                    return Promise.resolve({});
                  }}
                  pageSize={5}
                  ssrData={popularNews}
                  renderItem={(item) => <CardNews key={item.id} {...item} description={null} />}
                  renderSkeleton={() => <Skeleton.Card directional="horizontal" rtl />}
                  renderSeparator={() => (
                    <Separator backgroundColor="gray40" variant="horizontal" />
                  )}
                />
              </div>
              <Separator variant="three-dot-horizontal" />
            </>
          )}

          {recentNews?.content?.length > 0 && (
            <div id="gan-day" className={cl('section-block', 'sticky-on')}>
              <TitleH2DailyLearning className="font-inter">Gần đây</TitleH2DailyLearning>
              <PostListing
                className="recent-news-post-listing"
                apiKey={[apiCacheKeys.publicService.GET_RECENT_NEWS_LIST, seoData.id]}
                apiFn={(params) =>
                  getRecentNewsList({ ...params, excludedIds: excludedPopularNewsIds })
                }
                infiniteScrollEnabled
                pageSize={5}
                ssrData={recentNews}
                renderItem={(item) => <CardNews key={item.id} {...item} description={null} />}
                renderSkeleton={() => <Skeleton.Card directional="horizontal" rtl />}
                renderSeparator={() => <Separator backgroundColor="gray40" variant="horizontal" />}
                renderLoadMore={({ fetchNextPage }) => (
                  <Button.Primary onClick={fetchNextPage} type="primary" variant="outline">
                    Xem thêm bài viết
                  </Button.Primary>
                )}
              />
              {/* <DolenglishPromotionRightSideBar
                imgHorizontal={bannerHomePage.imgHorizontal}
                imgVertical={bannerHomePage.imgVertical}
                href={internalPageLink?.staticPageLink?.LANDING_HOME?.url || '/'}
              /> */}
            </div>
          )}
        </div>
      </div>

      <div className="desktop-sidebar">
        <DailyLearningLeftSideBar internalPageLink={internalPageLink} seoData={seoData} />
        <SideBarPageList
          title="Phổ biến"
          pageList={popularNews?.content.map((news) => {
            const { id, title, page } = news;
            return {
              id,
              title,
              url: page?.url || '/',
            };
          })}
        />
      </div>
      <ReferralShareBanner className="daily-news-detail" pageUrl={seoData?.url} />
      <InteractionBottomNavigation
        layout="responsive"
        pageId={seoData?.id}
        pageUrl={seoData?.url}
        scrollContainerElm={appScrollRef.current}
        tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title}
      />
    </Main>
  );
};

export const DailyNewsPreviewUI = compose(
  withWrapper(LandingLayoutDailyLearning),
  observer
)(DailyNewsPreviewBase);

const NoHeader = styled.div`
  #landing-header {
    display: none !important;
  }
`;

export const DailyNewsPreview = compose(
  withSamplePreviewStep,
  withWrapper(NoHeader, { isPreview: true }),
  observer
)(DailyNewsPreviewBase);
